import React, { useEffect, useState } from "react";
import MailIcon from "@material-ui/icons/Mail";
import PersonIcon from "@material-ui/icons/Person";
import RenewIcon from "@material-ui/icons/Autorenew";
import HistoryIcon from "@material-ui/icons/History";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Dialog, DialogActions, DialogTitle, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ManageSubscriptionsView.styles";
import SubscriptionModel from "../../../models/subscription/SubscriptionModel";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import SubscriptionFolderModel from "../../../models/subscription/SubscriptionFolderModel";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useHistory } from "react-router-dom";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { REPORT_SPECIAL_TYPE } from "../../../constants/REPORTS";

export interface IManageSubscriptionsViewProps extends WithStyles<typeof styles> { }

const ManageSubscriptionsViewPure: React.FunctionComponent<IManageSubscriptionsViewProps> = ({
    classes
}: IManageSubscriptionsViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const history = useHistory();
    const [editedSubscriptionIndex, setEditedSubscriptionIndex] = useState<number>(null);
    const [reinitializationDialogOpen, setReinitializationDialogOpen] = useState<boolean>(false);
    const [reinitializationAndCleanDialogOpen, setReinitializationAndCleanDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        dispatch(SubscriptionAction.requestAllSubscriptionFolders());
        dispatch(SubscriptionAction.requestAllSubscriptionTypes());
    }, [dispatch]);

    const subscriptionsFromState = useSelector(state => state.subscriptions);
    const isRequesting = useSelector(state =>
        selectRequesting(state, [
            SubscriptionAction.REQUEST_ALL_SUBSCRIPTIONS,
            SubscriptionAction.REQUEST_ALL_SUBSCRIPTION_FOLDERS
        ])
    );

    let subscriptions: IUniversalTableData;
    if (subscriptionsFromState?.allSubscriptions) {
        subscriptions = Object.values(subscriptionsFromState?.allSubscriptions)
            ?.map((subscription: SubscriptionModel) => {
                const actionsDisabled = [...new Set<string>([
                    ...(subscription.isFilterable ? ["reinitializeSubscription", "reinitializeAndClean", "edit"] : []),
                    ...(subscription.specialType === REPORT_SPECIAL_TYPE ? ["reinitializeSubscription", "reinitializeAndClean"] : [])]
                )];
                return {
                    id: subscription.id,
                    name: subscription.name,
                    description: subscription.description,
                    comment: subscription.comment,
                    isFilterable: subscription.isFilterable,
                    actionsDisabled
                }
            });
    }

    let subscriptionFolders: string[];
    if (subscriptionsFromState?.subscriptionFolders) {
        subscriptionFolders = Object.values(subscriptionsFromState.subscriptionFolders)
            ?.filter((subscription: SubscriptionFolderModel) => !subscription.active)
            ?.map((subscription: SubscriptionModel) => subscription.name);
    }

    const handleContactListClick = index => {
        const path = `/admin/manage-subscriptions/${subscriptions[index].id}/contacts`;
        history.push(path);
    };

    const handleUsersClick = index => {
        const path = `/admin/manage-subscriptions/${subscriptions[index].id}`;
        history.push(path);
    };

    const handleEditFinished = (index, newData) => {
        const updatedSubscription = {
            name: newData.name,
            description: newData.description,
            comment: newData.comment
        };
        dispatch(SubscriptionAction.updateSubscriptionType(subscriptions[index].id, updatedSubscription))
            .then(() => {
                dispatch(SubscriptionAction.requestAllSubscriptionTypes());
                dispatch(SubscriptionAction.requestAllSubscriptionFolders());
                enqueueSnackbar(t("admin.manageSubscriptionsView.subscriptionEdited"), {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar(t("admin.manageSubscriptionsView.subscriptionEditError") + ": " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleReinitializeClick = index => {
        setEditedSubscriptionIndex(index);
        setReinitializationDialogOpen(true);
    };

    const handleReinitialization = () => {
        dispatch(SubscriptionAction.reinitializeSubscriptionType(subscriptions[editedSubscriptionIndex].id)).then(() => {
            setEditedSubscriptionIndex(null);
            setReinitializationDialogOpen(false);
            enqueueSnackbar("Missing files have been initialized.", {
                variant: ToastStatusEnum.Info
            });
        })
            .catch(error =>
                enqueueSnackbar("Initialization of missing files was not successful: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleReinitializeAndCleanClick = index => {
        setEditedSubscriptionIndex(index);
        setReinitializationAndCleanDialogOpen(true);
    };

    const handleReinitializationAndClean = () => {
        dispatch(SubscriptionAction.reinitializeAndClean(subscriptions[editedSubscriptionIndex].id)).then(() => {
            setEditedSubscriptionIndex(null);
            setReinitializationAndCleanDialogOpen(false);
            enqueueSnackbar("All subscription files have been deleted and recreated.", {
                variant: ToastStatusEnum.Info
            });
        })
            .catch(error =>
                enqueueSnackbar("Deletion and recreation were not successful: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    }


    const handleAddFinished = newSubscriptionType => {
        dispatch(SubscriptionAction.createSubscriptionType(newSubscriptionType))
            .then(() => {
                dispatch(SubscriptionAction.requestAllSubscriptionTypes());
                dispatch(SubscriptionAction.requestAllSubscriptionFolders());
                enqueueSnackbar(t("admin.manageSubscriptionsView.subscriptionAdded"), {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar(t("admin.manageSubscriptionsView.subscriptionAddError") + ": " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };


    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.name"),
            field: "name",
            editable: "onAdd"
        },
        {
            type: "text",
            title: t("general.description"),
            field: "description",
            validate: rowData =>
                !rowData?.description || rowData?.description === ""
                    ? { isValid: false, helperText: "Please enter a description" }
                    : true
        },
        {
            type: "multiline_text",
            title: "Comment",
            field: "comment",
            editable: "always"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "reinitializeSubscription",
                    icon: <RenewIcon />,
                    tooltip: "Initialize missing files",
                    onClick: index => handleReinitializeClick(index),
                    scope: "subscriptions.write",
                    disabledTooltip: "The initialization of missing files is disabled for filterable products"
                },
                {
                    name: "reinitializeAndClean",
                    icon: <HistoryIcon />,
                    tooltip: "Delete all and recreate all subscription files",
                    onClick: index => handleReinitializeAndCleanClick(index),
                    scope: "subscriptions.write",
                    disabledTooltip: "The recreation of all subscription files is disabled for filterable products"
                },
                {
                    name: "contactList",
                    icon: <MailIcon />,
                    tooltip: t("general.contactList"),
                    onClick: index => handleContactListClick(index),
                    scope: "subscriptions.write"
                },
                {
                    name: "users",
                    icon: <PersonIcon />,
                    tooltip: t("general.users"),
                    onClick: index => handleUsersClick(index),
                    scope: "subscriptions.users.read"
                },
                {
                    name: "edit",
                    editFields: [
                        {
                            field: "name",
                            options: subscriptionFolders,
                            getAdditionalRowOptions: index => [subscriptions?.[index]?.name as string]
                        }
                    ],
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEditFinished(index, newValue),
                    scope: "subscriptions.write",
                    disabledTooltip: "The edit is disabled for filterable products"
                },
                {
                    name: "add",
                    disabled: subscriptionFolders?.length === 0,
                    tooltip: t("admin.manageSubscriptionsView.addSubscription"),
                    onAddFinished: newValue => handleAddFinished(newValue),
                    disabledTooltip: "There is no subscription product to be added",
                    scope: "subscriptions.write"
                }
            ]
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: t("admin.manageSubscriptionsView.manageSubscriptions") }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h4">{t("admin.manageSubscriptionsView.manageSubscriptions")}</Typography>
            </div>
            <UniversalTable columns={columns} data={subscriptions} isLoading={isRequesting} searchable />

            <Dialog open={reinitializationDialogOpen}>
                <DialogTitle id="confirm-reinitialization-dialog">
                    Are you sure you want to initialize missing files for subscription type {subscriptions?.[editedSubscriptionIndex]?.name}?
                </DialogTitle>
                <DialogActions>
                    <BlueButton onClick={handleReinitialization}>
                        Yes
                    </BlueButton>
                    <BlueButton onClick={() => setReinitializationDialogOpen(false)}>
                        No
                    </BlueButton>
                </DialogActions>
            </Dialog>

            <Dialog open={reinitializationAndCleanDialogOpen}>
                <DialogTitle id="confirm-reinitialization-and-clean-dialog">
                    Are you sure you want to delete all and recreate all subscription files within subscription type {subscriptions?.[editedSubscriptionIndex]?.name}?
                </DialogTitle>
                <DialogActions>
                    <BlueButton onClick={handleReinitializationAndClean}>
                        Yes
                    </BlueButton>
                    <BlueButton onClick={() => setReinitializationAndCleanDialogOpen(false)}>
                        No
                    </BlueButton>
                </DialogActions>
            </Dialog>

        </CenteredContainer>
    );
};

export const ManageSubscriptionsView = withStyles(styles)(ManageSubscriptionsViewPure);
